import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MessageSquare, HelpCircle, Sun, Moon, Loader2, LayoutDashboard } from 'lucide-react';
import ConversationService, { ConversationSummary } from '../services/conversationService';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  onStartNewChat: () => void;
  onOpenSettings?: () => void;
  settingsLabel?: string;
  brandName?: string;
  brandIconUrl?: string;
  adminUsername?: string | null;
  adminEmail?: string | null;
}

function formatRelativeTime(isoDate: string): string {
  if (!isoDate) return '';
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `Vor ${diffMin} Min.`;
  if (diffHr < 24) return `Vor ${diffHr} Std.`;
  if (diffDay === 1) return 'Gestern';
  if (diffDay < 7) return `Vor ${diffDay} Tagen`;
  if (diffDay < 30) return `Vor ${Math.floor(diffDay / 7)} Wochen`;
  return date.toLocaleDateString('de-DE');
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  onToggleTheme,
  onStartNewChat,
  onOpenSettings,
  settingsLabel = 'Einstellungen',
  brandName = 'Synaworks',
  brandIconUrl = '/favicon-synaworks.png',
  adminUsername,
  adminEmail,
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    const service = ConversationService.getInstance();
    service.listConversations()
      .then(setConversations)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleConversationClick = (e: React.MouseEvent, chatId: string) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    onClose();
    navigate(`/c/${chatId}`);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-screen w-80 bg-gray-900 z-50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                onClose();
                navigate('/');
              }}
              className="flex items-center gap-3 rounded-lg p-1 -m-1 text-left hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
                <img
                  src={brandIconUrl}
                  alt={brandName}
                  className="w-5 h-5 object-contain"
                />
              </div>
              <span className="text-white font-semibold">{brandName}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-800 text-gray-400"
            >
              <X size={20} />
            </button>
          </div>

        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-gray-400 text-sm font-medium mb-3">Vorherige Chats</h3>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="text-gray-500 animate-spin" />
            </div>
          )}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
          {!loading && !error && conversations.length === 0 && (
            <p className="text-gray-500 text-sm">Noch keine Gespräche vorhanden.</p>
          )}
          <div className="space-y-2">
            {conversations.map((conv) => (
              <a
                key={conv.chat_id}
                href={`/c/${conv.chat_id}`}
                onClick={(e) => handleConversationClick(e, conv.chat_id)}
                className="block w-full text-left p-3 rounded-lg hover:bg-gray-800 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <MessageSquare size={16} className="text-gray-500 mt-0.5 group-hover:text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-300 text-sm font-medium truncate">
                      {conv.preview || 'Neues Gespräch'}
                    </div>
                    <div className="text-gray-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
                      <span>{formatRelativeTime(conv.last_modified)}</span>
                      <span>&middot;</span>
                      <span>{conv.message_count} Nachr.</span>
                      {conv.username && (
                        <>
                          <span>&middot;</span>
                          <span className="text-violet-400 truncate max-w-[100px]">{conv.username}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Menu */}
        <div className="p-4 border-t border-gray-700">
          <div className="space-y-2">
            {onOpenSettings && (
              <button
                onClick={() => {
                  onOpenSettings();
                  onClose();
                }}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
              >
                <span className="text-sm">{settingsLabel}</span>
              </button>
            )}
            <button
              onClick={() => { onClose(); navigate('/admin'); }}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
            >
              <LayoutDashboard size={18} />
              <span className="text-sm">Admin-Panel</span>
            </button>
            <div className="flex items-center justify-between">
              <button
                onClick={() => window.open('https://synaworks.com/kontakt/', '_blank')}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 text-gray-300 hover:text-white transition-colors flex-1"
              >
                <HelpCircle size={18} />
                <span className="text-sm">Hilfe & Support</span>
              </button>
              <button
                onClick={onToggleTheme}
                className="p-2 rounded-lg hover:bg-gray-800 text-gray-300 hover:text-white transition-colors"
                title={isDarkMode ? 'Hellmodus' : 'Dunkelmodus'}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-gray-300 text-sm font-medium">
                  {(adminUsername ?? 'A').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-300 text-sm font-medium">{adminUsername ?? 'Admin'}</div>
                {adminEmail && (
                  <div className="text-gray-500 text-xs truncate">{adminEmail}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
